/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { ReferralInsurance } from "./referrals";
import { api, apiBlob, mutatingPost, type ApiResult } from "./client";
import { PAGE_SIZE } from "./registry";
import type { Paged } from "./registry";

export const FAX_VERDICT_LABELS: Record<string, string> = {
  MATCH: "Existing",
  NEW_RECORD: "New",
  AMBIGUOUS: "Ambiguous",
  REJECTED: "Rejected",
  UNRESOLVED: "Unresolved",
};

/** Why intake left a case for a human instead of creating the referral unattended. */
export const FAX_REFUSAL_REASONS: Record<string, string> = {
  INTAKE_FAILED: "Intake could not finish, so there was nothing to create from.",
  NO_CONFIDENCE: "The extraction returned no confidence score.",
  BELOW_THRESHOLD: "Confidence was below the auto-create threshold.",
  ORGANIZATION_UNRESOLVED: "No referring organization could be resolved.",
  PROVIDER_UNRESOLVED: "No referring provider could be resolved.",
  ORGANIZATION_AMBIGUOUS:
    "More than one organization matched, so creating one would risk a duplicate.",
  PROVIDER_AMBIGUOUS: "More than one provider matched, so creating one would risk a duplicate.",
  PROVIDER_NOT_IN_ORGANIZATION:
    "The matched provider belongs to a different organization than the one matched on this fax."
    + " Choose the organization they belong to, or create the provider under the one named on the fax.",
};

export type FaxStatus = "PENDING" | "RESOLVED";

export type FaxSummary = {
  id: string;
  ehrFaxId: string;
  fromNumber: string | null;
  receivedAt: string | null;
  status: FaxStatus;
  confidence: number | null;
  organizationVerdict: string | null;
  providerVerdict: string | null;
  resolution: string | null;
  referralId: number | null;
  intakeFailed: boolean;
  autoCreateRefusal: string | null;
  createdAt: string | null;
};

export type FaxExtraction = {
  patient: {
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    stateCode: string | null;
  } | null;
  guardian: {
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
  referringProvider: {
    firstName: string | null;
    lastName: string | null;
    npi: string | null;
    email: string | null;
    faxNumber: string | null;
    phoneNumber: string | null;
    credential: string | null;
  } | null;
  referringOrganization: {
    name: string | null;
    stateCode: string | null;
    address: string | null;
    phone: string | null;
    fax: string | null;
    emailDomain: string | null;
  } | null;
  referral: {
    servicesRequested: string[] | null;
    reasonForReferral: string | null;
    icdCodes: string[] | null;
    notes: string | null;
  } | null;
  insurance: {
    payerName: string | null;
    subscriberId: string | null;
    groupNumber: string | null;
    payerId: string | null;
  } | null;
  confidence: number;
  extractionNotes: string | null;
};

export type FaxCandidate = Record<string, unknown>;

export type ReviewPerson = {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  phone: string | null;
  email: string | null;
  stateCode: string | null;
};

export type FaxReviewInput = {
  patient: ReviewPerson | null;
  guardian: ReviewPerson | null;
  organization:
    | { existingId: number; create: null }
    | { existingId: null; create: { name: string; stateCode: string | null; emailDomain: string | null } }
    | null;
  provider:
    | { existingId: number; create: null }
    | {
        existingId: null;
        create: {
          firstName: string | null;
          lastName: string | null;
          email: string | null;
          phoneNumber: string | null;
          faxNumber: string | null;
        };
      }
    | null;
  note: string | null;
  insurance: ReferralInsurance | null;
  concerns: string | null;
};

export type FaxDetail = {
  summary: FaxSummary;
  extraction: FaxExtraction | null;
  organizationCandidates: FaxCandidate[] | null;
  organizationId: number | null;
  providerCandidates: FaxCandidate[] | null;
  providerId: number | null;
  providerOrganizationId: number | null;
  intakeError: string | null;
  resolvedBy: number | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  reviewInput: FaxReviewInput | null;
};

export function listFaxes(
  status: FaxStatus | null,
  page: number,
): Promise<ApiResult<Paged<FaxSummary>>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (status) {
    params.set("status", status);
  }
  return api<Paged<FaxSummary>>(`/v1/referrals/faxes?${params.toString()}`);
}

export function faxDetail(id: string): Promise<ApiResult<FaxDetail>> {
  return api<FaxDetail>(`/v1/referrals/faxes/${encodeURIComponent(id)}`);
}

export function faxPdf(id: string, signal: AbortSignal): Promise<ApiResult<Blob>> {
  return apiBlob(`/v1/referrals/faxes/${encodeURIComponent(id)}/pdf`, { signal });
}

export function createReferralFromFax(
  id: string,
  review: FaxReviewInput,
): Promise<ApiResult<FaxDetail>> {
  return api<FaxDetail>(
    `/v1/referrals/faxes/${encodeURIComponent(id)}/actions/create-referral`,
    mutatingPost(review),
  );
}

export function resolveFax(id: string, note: string): Promise<ApiResult<FaxDetail>> {
  return api<FaxDetail>(
    `/v1/referrals/faxes/${encodeURIComponent(id)}/actions/resolve`,
    mutatingPost({ note }),
  );
}
